import React, { useCallback, useState } from 'react';

import { SendAlarmButton } from '@vlabs/monokit/features/send-alarm';
import type { DetectionOverlaySettingsValue } from '@vlabs/monokit/shared/ui/detection-overlay';
import { DetectionOverlaySettings } from '@vlabs/monokit/shared/ui/detection-overlay';
import { useImage } from '@vlabs/shared/hooks';
import {
  getSavedVideoAnalyticOverlayColor,
  saveVideoAnalyticOverlayColor,
} from '@vlapps/point/src/utils/videoAnalyticOverlayColor';
import type { VideoAnalyticOverlayColor } from '@vlapps/point/src/utils/videoAnalyticOverlayColor';
import { useTranslation } from 'react-i18next';

import st from './BaseVideoAnalyticsDetails.module.sass';
import { VideoAnalyticsEventInfo } from './VideoAnalyticsEventInfo';
import { VideoAnalyticsFrame } from './VideoAnalyticsFrame';
import type { AnalyticDetailsEvent, DetectionType, OverlayDetection } from '../types';

interface BaseVideoAnalyticsDetailsProps {
  event: AnalyticDetailsEvent;
  title: string;
  tags?: React.ReactNode;
  extraRows?: React.ReactNode;
  detectionType?: DetectionType;
  detections?: OverlayDetection[];
  detectionSettingsLabel?: string;
}

export function BaseVideoAnalyticsDetails({
  event,
  title,
  tags = undefined,
  extraRows = undefined,
  detectionType = undefined,
  detections = undefined,
  detectionSettingsLabel = undefined,
}: BaseVideoAnalyticsDetailsProps): React.JSX.Element {
  const { t } = useTranslation();

  const settingsLabel = detectionSettingsLabel
    ?? t(`videoAnalytics:${event.event_type}AnalyticDetails.workspace.detection switch.label`);

  const [color, setColor] = useState(getSavedVideoAnalyticOverlayColor);
  const [dotSize, setDotSize] = useState<number | undefined>();
  const [isDetectionVisible, setIsDetectionVisible] = useState(true);

  const canRenderDetections = !!detectionType;

  const fullFrame = useImage(event?.event?.overview?.image);

  const handleColorChange = useCallback((nextColor: VideoAnalyticOverlayColor) => {
    setColor(nextColor);
    saveVideoAnalyticOverlayColor(nextColor);
  }, []);
  const handleChangeSettings = useCallback((nextSettings: DetectionOverlaySettingsValue) => {
    setIsDetectionVisible(nextSettings.withDetections);
    setDotSize(nextSettings.dotSize);

    if (nextSettings.color) {
      handleColorChange(nextSettings.color);
    }
  }, [handleColorChange]);

  const overlay = {
    type: detectionType,
    coordinates: detections,
    color,
    dotSize,
  };

  return (
    <div className={st.Root}>
      <VideoAnalyticsFrame
        detections={overlay}
        imageUrl={fullFrame.url}
        withDetections={isDetectionVisible}
      />

      <div className={st.Details}>
        <div className={st.Content}>
          <div>
            <div className={st.Header}>
              <p className={st.Title}>{title}</p>
            </div>

            <div className={st.InfoBlock}>
              <div className={st.Tags}>
                {tags}
              </div>

              <VideoAnalyticsEventInfo
                event={event}
                extraRows={extraRows}
              />
            </div>
          </div>
          <SendAlarmButton eventId={event.event.event_id} />
        </div>

        {!!canRenderDetections && !!fullFrame.url && (
          <div className={st.ControlsBlock}>
            <DetectionOverlaySettings
              className={st.Settings}
              data-testid="workspace.analyticEventDetails.overlaySettings"
              label={settingsLabel ?? ''}
              onChangeSettings={handleChangeSettings}
              settings={{
                withDetections: isDetectionVisible,
                withColor: true,
                color,
                withSize: detectionType === 'point',
                dotSize,
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
